import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import * as Sentry from '@sentry/react';
import { BrowserRouter } from 'react-router-dom';

import store from './reducers/store';
import '@fontsource/open-sans/cyrillic-700.css';
import '@fontsource/open-sans/cyrillic-400.css';
import '@fontsource/open-sans/cyrillic-ext-700.css';
import '@fontsource/open-sans/cyrillic-ext-400.css';
import '@fontsource/open-sans/latin-ext-700.css';
import '@fontsource/open-sans/latin-ext-400.css';

import '/node_modules/flag-icons/css/flag-icons.min.css';
import '@microcharts/react/styles.css';
import ThemeProvider from '@tcc-online/react-theme-provider';

const container = document.getElementById('root')!;

(async () => {
    try {
        const { config } = await import('./config');
        const { initSentry } = await import('./sentry/initSentry');
        initSentry(config);
        const { configureRequestService } =
            await import('./setup/configureRequestService');
        const { appRequestService } = await import('./setup/appRequestService');
        const { RequestServiceProvider } =
            await import('./setup/RequestServiceProvider');
        configureRequestService();
        const { syncAuthAcrossTabs } =
            await import('./setup/syncAuthAcrossTabs');
        syncAuthAcrossTabs(store);
        const { default: App } = await import('./App');

        const root = createRoot(container, {
            onUncaughtError: Sentry.reactErrorHandler(),
            onCaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
                console.warn('Caught error:', error, errorInfo.componentStack);
            }),
        });

        root.render(
            <Sentry.ErrorBoundary>
                <Provider store={store}>
                    <RequestServiceProvider value={appRequestService}>
                        <BrowserRouter>
                            <ThemeProvider>
                                <App />
                            </ThemeProvider>
                        </BrowserRouter>
                    </RequestServiceProvider>
                </Provider>
            </Sentry.ErrorBoundary>,
        );
    } catch (error) {
        console.error('Application bootstrap failed:', error);
        if (Sentry.getClient()) {
            Sentry.captureException(error);
        }
        container.textContent =
            'Application failed to load. Please contact support.';
    }
})();
